export const PLAYER_VS_PLAYER_PROP_TYPES = [
  "PLAYER_VS_PLAYER_YARDS",
  "PLAYER_VS_PLAYER_RECEPTIONS",
  "PLAYER_VS_PLAYER_CARRIES",
] as const;

export const lastNameFromFullName = (fullName: string): string => {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  return parts[parts.length - 1] ?? fullName;
};

export function isHeadToHeadProp(propType: string, playerCount: number): boolean {
  return (
    PLAYER_VS_PLAYER_PROP_TYPES.includes(propType as (typeof PLAYER_VS_PLAYER_PROP_TYPES)[number])
    || (propType === "LONGEST_STAT" && playerCount >= 2)
  );
}

type SideOption = {
  playerId?: unknown;
  label?: unknown;
};

const optionPlayerId = (option: SideOption): string | null =>
  typeof option.playerId === "string" && option.playerId.trim() ? option.playerId.trim() : null;

const optionLabel = (option: SideOption): string =>
  typeof option.label === "string" ? option.label.toLowerCase() : "";

export function orderPlayersForSides<T extends { id: string; fullName?: string | null }>(
  players: T[],
  displayConfig?: Record<string, unknown> | null,
): T[] {
  if (players.length < 2) return players;
  const options = Array.isArray(displayConfig?.["options"]) ? displayConfig["options"] : [];
  const byId = new Map(players.map((player) => [player.id, player]));
  const used = new Set<string>();
  const ordered: T[] = [];

  const push = (player: T | undefined) => {
    if (!player || used.has(player.id)) return;
    used.add(player.id);
    ordered.push(player);
  };

  for (const option of options) {
    if (!option || typeof option !== "object") continue;
    const record = option as SideOption;
    const playerId = optionPlayerId(record);
    if (playerId) {
      push(byId.get(playerId));
      continue;
    }
    const label = optionLabel(record);
    if (!label) continue;
    push(players.find((player) => {
      const name = (player.fullName ?? "").toLowerCase();
      if (!name) return false;
      const last = lastNameFromFullName(name);
      return (last.length > 1 && label.includes(last)) || label.includes(name);
    }));
  }

  for (const player of players) push(player);
  return ordered;
}

export function orderPlayerIdsForSides(
  playerIds: string[],
  displayConfig?: Record<string, unknown> | null,
): string[] {
  const options = Array.isArray(displayConfig?.["options"]) ? displayConfig["options"] : [];
  const remaining = [...playerIds];
  const ordered: string[] = [];
  for (const option of options) {
    if (!option || typeof option !== "object") continue;
    const playerId = optionPlayerId(option as SideOption);
    if (!playerId) continue;
    const index = remaining.indexOf(playerId);
    if (index < 0) continue;
    ordered.push(playerId);
    remaining.splice(index, 1);
  }
  return [...ordered, ...remaining];
}

export function longestStatNeedsThreshold(playerCount: number): boolean {
  return playerCount < 2;
}

export type PropStatLineDefinition = {
  propType: string;
  threshold?: number | string | null;
  displayConfig?: Record<string, unknown> | null;
  players: { id: string; fullName: string }[];
};

const numericThreshold = (value: number | string | null | undefined): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const matchupLine = (
  players: { id: string; fullName: string }[],
  currentValues: Record<string, number>,
): string | null => {
  const first = players[0];
  const second = players[1];
  if (!first || !second) return null;
  return `${lastNameFromFullName(first.fullName)} ${currentValues[first.id] ?? 0} • ${lastNameFromFullName(second.fullName)} ${currentValues[second.id] ?? 0}`;
};

export type PropStatHeaderRow = {
  id: string;
  label: string;
  value: string;
};

const headerStatValue = (
  currentValues: Record<string, number> | null,
  key: string,
): string => {
  if (!currentValues) return "—";
  const value = currentValues[key];
  return typeof value === "number" && Number.isFinite(value) ? String(value) : "0";
};

const playerHeaderRow = (
  player: { id: string; fullName: string },
  currentValues: Record<string, number> | null,
): PropStatHeaderRow => ({
  id: player.id,
  label: lastNameFromFullName(player.fullName),
  value: headerStatValue(currentValues, player.id),
});

export function formatPropStatLine(
  definition: PropStatLineDefinition,
  currentValues: Record<string, number>,
): string | null {
  const players = orderPlayersForSides(definition.players, definition.displayConfig);
  const thresholdValue = numericThreshold(definition.threshold);

  switch (definition.propType) {
    case "PLAYER_VS_PLAYER_YARDS":
    case "PLAYER_VS_PLAYER_RECEPTIONS":
    case "PLAYER_VS_PLAYER_CARRIES":
      return matchupLine(players, currentValues);
    case "PLAYER_OVER_UNDER_YARDS":
    case "PLAYER_OVER_UNDER_RECEPTIONS":
    case "PLAYER_OVER_UNDER_CARRIES":
    case "PLAYER_OVER_UNDER_INTERCEPTIONS":
    case "PLAYER_OVER_UNDER_SACKS": {
      const player = players[0];
      if (!player) return null;
      const value = currentValues[player.id] ?? 0;
      if (thresholdValue === null) return `${lastNameFromFullName(player.fullName)} ${value}`;
      return `${lastNameFromFullName(player.fullName)} ${value} / ${thresholdValue}`;
    }
    case "PLAYER_EITHER_REACHES_THRESHOLD": {
      const playerValues = players.map((player) =>
        `${lastNameFromFullName(player.fullName)} ${currentValues[player.id] ?? 0}`,
      );
      if (thresholdValue === null) return playerValues.join(" • ");
      return `${playerValues.join(" • ")} / ${thresholdValue}`;
    }
    case "ANYTIME_TD":
    case "RUSHING_TD":
    case "RECEIVING_TD":
    case "PASSING_TD": {
      const player = players[0];
      if (!player) return null;
      const value = currentValues[player.id] ?? 0;
      const label =
        definition.propType === "RUSHING_TD"
          ? "Rush TDs"
          : definition.propType === "RECEIVING_TD"
            ? "Rec TDs"
            : definition.propType === "PASSING_TD"
              ? "Pass TDs"
              : "TDs";
      return `${lastNameFromFullName(player.fullName)} ${label} ${value}`;
    }
    case "ANYTIME_TD_MULTI": {
      if (!players.length) return null;
      return players
        .map((player) => `${lastNameFromFullName(player.fullName)} ${currentValues[player.id] ?? 0}`)
        .join(" • ") || null;
    }
    case "LONGEST_STAT": {
      if (players.length >= 2) return matchupLine(players, currentValues);
      const player = players[0];
      if (!player) return null;
      const value = currentValues[player.id] ?? 0;
      if (thresholdValue === null) return `${lastNameFromFullName(player.fullName)} ${value}`;
      return `${lastNameFromFullName(player.fullName)} ${value} / ${thresholdValue}`;
    }
    default:
      return null;
  }
}

const SINGLE_PLAYER_LINE_PROP_TYPES = new Set([
  "PLAYER_OVER_UNDER_YARDS",
  "PLAYER_OVER_UNDER_RECEPTIONS",
  "PLAYER_OVER_UNDER_CARRIES",
  "PLAYER_OVER_UNDER_INTERCEPTIONS",
  "PLAYER_OVER_UNDER_SACKS",
]);

export function formatPropStatHeaderRows(
  definition: PropStatLineDefinition,
  currentValues: Record<string, number> | null,
): PropStatHeaderRow[] | null {
  const players = orderPlayersForSides(definition.players, definition.displayConfig);
  if (!players.length) return null;

  const rows = players.map((player) => playerHeaderRow(player, currentValues));
  const thresholdValue = numericThreshold(definition.threshold);
  const showLine = thresholdValue !== null && (
    (definition.propType === "LONGEST_STAT" && players.length < 2)
    || (SINGLE_PLAYER_LINE_PROP_TYPES.has(definition.propType) && players.length === 1)
  );
  if (showLine && thresholdValue !== null) {
    rows.push({ id: "line", label: "Line", value: String(thresholdValue) });
  }
  return rows;
}
